import React, { useMemo } from 'react';
import { partition } from 'lodash';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import {
  Collapsible,
  CollapsibleHeader,
  CollapsibleContent,
  CollapsibleHeaderChevron,
} from '@fiverr-private/disclosure';

import { responsiveDisplay } from '../../util';
import { Chip, isAttachmentsSection } from '../../../common/Chips';
import { useIncludeSection } from '../useIncludeSection';
import { BriefSection } from '../../../../types';
import { disabledBriefEditorOpacity } from '../../../../common/constants';
import { DEFAULT_SECTIONS_GROUP } from '../constants';
import { showBoxShadowTopOnly } from './styles.ve.css';

const COPY_PREFIX = 'ai_brief.edit_brief';

interface ExcludedSectionsNavBarProps {
  sections: BriefSection[];
  disabled?: boolean;
  shouldShowSidebar?: boolean;
}

export const ExcludedSectionsNavBar = ({ sections, disabled, shouldShowSidebar }: ExcludedSectionsNavBarProps) => {
  const includeSection = useIncludeSection();
  const { isMobile } = getContext();

  const orderedSections = useMemo(() => {
    const [attachmentsSections, otherSections] = partition(sections, isAttachmentsSection);
    const [attachmentsSection] = attachmentsSections;

    return [...(attachmentsSection ? [attachmentsSection] : []), ...otherSections];
  }, [sections]);

  if (sections.length === 0) {
    return null;
  }

  const handleIncludeSection = (section: BriefSection) => () => {
    includeSection(section);
  };

  if (!isMobile) {
    return (
      <Container
        className={showBoxShadowTopOnly}
        boxShadow="z2"
        display={shouldShowSidebar ? responsiveDisplay('flex', false) : 'flex'}
        direction="column"
        style={{
          opacity: disabled ? disabledBriefEditorOpacity : '1',
        }}
      >
        <Stack direction="column" padding="3" gap="3" flexShrink={0} pointerEvents={disabled ? 'none' : undefined}>
          <Text color="bodySecondary" size="b_xs">
            <I18n k={`${COPY_PREFIX}.${DEFAULT_SECTIONS_GROUP}`} />
          </Text>
          <Stack direction="row" overflowX="auto" gap="2">
            {orderedSections.map((section) => (
              <Chip key={section.id} title={section.title ?? section.id} onClick={() => includeSection(section)} />
            ))}
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container paddingX="2" borderTopWidth={'sm'}>
      <Container
        className={showBoxShadowTopOnly}
        boxShadow="z2"
        direction="column"
        style={{
          opacity: disabled ? disabledBriefEditorOpacity : '1',
        }}
      >
        <Collapsible>
          <CollapsibleHeader paddingY={'0'}>
            <Text color="bodySecondary" size="b_sm" fontWeight="bold" paddingY="2">
              <I18n k={`${COPY_PREFIX}.${DEFAULT_SECTIONS_GROUP}`} />
            </Text>
            <CollapsibleHeaderChevron />
          </CollapsibleHeader>

          <CollapsibleContent>
            <Container direction="column">
              <Stack
                direction="column"
                padding="3"
                paddingTop="0"
                gap="3"
                flexShrink={0}
                pointerEvents={disabled ? 'none' : undefined}
              >
                <Stack direction="row" overflowX="auto" gap="2">
                  {orderedSections.map((section) => (
                    <Chip
                      key={section.id}
                      title={section.title ?? section.id}
                      onClick={handleIncludeSection(section)}
                    />
                  ))}
                </Stack>
              </Stack>
            </Container>
          </CollapsibleContent>
        </Collapsible>
      </Container>
    </Container>
  );
};
